import React, { useRef, useState, useCallback } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, AreaChart, Area
} from 'recharts';
import { IconButton, TooltipHost, DirectionalHint, Spinner, SpinnerSize, Dropdown, IDropdownOption, Toggle } from '@fluentui/react';
import { ArrowDownload20Regular, Image20Regular, Table20Regular, ChartMultiple20Regular } from '@fluentui/react-icons';
import { saveAs } from 'file-saver';
import styles from './Dashboard.module.css';

// Chart types that can be switched between
type SwitchableChartType = 'bar' | 'pie' | 'line' | 'area' | 'horizontalBar' | 'donut';

// Define which chart types can switch to which
const SWITCHABLE_TYPES: SwitchableChartType[] = ['bar', 'horizontalBar', 'pie', 'donut', 'line', 'area'];

const CHART_TYPE_OPTIONS: IDropdownOption[] = [
  { key: 'bar', text: 'Bar Chart' },
  { key: 'horizontalBar', text: 'Horizontal Bar' },
  { key: 'pie', text: 'Pie Chart' },
  { key: 'donut', text: 'Donut Chart' },
  { key: 'line', text: 'Line Chart' },
  { key: 'area', text: 'Area Chart' },
];

// Data Table Component for showing raw data
const DataTableView: React.FC<{ data: any[]; title: string; format?: string }> = ({ data, title, format }) => {
  if (!data || data.length === 0) {
    return <div className={styles.noData}>No data available</div>;
  }

  // Get column headers from first data item
  const columns = Object.keys(data[0]).filter(key => key !== 'type'); // Exclude internal 'type' field
  
  const formatCellValue = (value: any, column: string): string => {
    if (value === undefined || value === null) return '-';
    if (typeof value === 'number') {
      if (format === 'currency' || column.toLowerCase().includes('revenue') || column.toLowerCase().includes('sales')) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value);
      }
      return value.toLocaleString();
    }
    return String(value);
  };

  const handleExportCSV = () => {
    const headers = columns.join(',');
    const rows = data.map(row => 
      columns.map(col => {
        const val = row[col];
        // Escape quotes and wrap in quotes if contains comma
        if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      }).join(',')
    );
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${title.replace(/[^a-zA-Z0-9]/g, '_')}_data.csv`);
  };

  return (
    <div className={styles.dataTableContainer}>
      <div className={styles.dataTableHeader}>
        <span className={styles.dataCount}>{data.length} rows</span>
        <TooltipHost content="Export to CSV">
          <IconButton 
            className={styles.exportDataButton}
            onClick={handleExportCSV}
            title="Export to CSV"
          >
            <ArrowDownload20Regular />
          </IconButton>
        </TooltipHost>
      </div>
      <div className={styles.dataTableScroll}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              {columns.map((col, i) => (
                <th key={i}>{col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((col, colIndex) => (
                  <td key={colIndex}>{formatCellValue(row[col], col)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface KPIData {
  metric: string;
  value: number;
  average: number;
  max: number;
  min: number;
  format: 'currency' | 'number';
}

interface WidgetData {
  type: 'kpi' | 'bar' | 'pie' | 'line' | 'heatmap' | 'performance';
  title: string;
  data: any[];
  xLabel?: string;
  yLabel?: string;
  format?: string;
  layout?: {
    size: 'small' | 'medium' | 'large';
    position: 'top' | 'auto';
  };
  rows?: string[];
  columns?: string[];
  values?: number[][];
  metric?: string;
}

interface DashboardData {
  title: string;
  widgets: WidgetData[];
  summary: {
    total_rows: number;
    total_columns: number;
    numeric_columns: number;
    categorical_columns: number;
    date_columns: number;
  };
  insights: string[];
}

interface DashboardProps {
  dashboardData: DashboardData;
}

const CHART_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

const formatValue = (value: number, format?: string): string => {
  if (format === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }
  return value.toLocaleString();
};

const KPIWidget: React.FC<{ data: KPIData[] }> = ({ data }) => {
  return (
    <div className={styles.kpiGrid}>
      {data.map((kpi, index) => (
        <div key={index} className={styles.kpiCard}>
          <div className={styles.kpiMetric}>{kpi.metric}</div>
          <div className={styles.kpiValue}>
            {formatValue(kpi.value, kpi.format)}
          </div>
          <div className={styles.kpiStats}>
            <div>Avg: {formatValue(kpi.average, kpi.format)}</div>
            <div>Max: {formatValue(kpi.max, kpi.format)}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const ChartWidget: React.FC<{ widget: WidgetData; widgetIndex: number; chartTypeOverrides: Record<number, SwitchableChartType>; onTypeChange: (index: number, type: SwitchableChartType) => void }> = ({ 
  widget, 
  widgetIndex, 
  chartTypeOverrides, 
  onTypeChange 
}) => {
  // State for showing data table vs chart
  const [showData, setShowData] = useState(false);
  
  // Determine if this widget type is switchable
  const isSwitchable = widget.type === 'bar' || widget.type === 'pie' || widget.type === 'line';
  
  // Determine if this widget has viewable data
  const hasViewableData = widget.data && Array.isArray(widget.data) && widget.data.length > 0;
  
  // Get the current chart type (override or original)
  const currentType = chartTypeOverrides[widgetIndex] || widget.type;
  
  const getWidgetHeight = () => {
    switch (widget.layout?.size) {
      case 'small': return '180px';
      case 'large': return '320px';
      case 'medium':
      default: return '240px';
    }
  };

  const renderChart = () => {
    const typeToRender = isSwitchable ? currentType : widget.type;
    console.log(`🎨 Rendering chart type: ${typeToRender} (original: ${widget.type})`);
    
    switch (typeToRender) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={widget.data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="label" 
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                tickFormatter={(value) => formatValue(value, widget.format)}
              />
              <Tooltip 
                formatter={(value: number) => [formatValue(value, widget.format), widget.yLabel || 'Value']}
              />
              <Bar dataKey="value" fill="#0078d4" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'horizontalBar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={widget.data} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={(value) => formatValue(value, widget.format)} />
              <YAxis type="category" dataKey="label" width={100} />
              <Tooltip 
                formatter={(value: number) => [formatValue(value, widget.format), widget.yLabel || 'Value']}
              />
              <Bar dataKey="value" fill="#0078d4" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <PieChart margin={{ top: 20, right: 5, bottom: 5, left: 5 }}>
              <Pie
                data={widget.data}
                cx="50%"
                cy="45%"
                labelLine={false}
                label={({ percent }: any) => `${(percent * 100).toFixed(0)}%`}
                outerRadius={65}
                innerRadius={0}
                fill="#8884d8"
                dataKey="value"
                nameKey="label"
              >
                {widget.data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: number, name: string) => [formatValue(value, widget.format || 'number'), name]} 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '12px'
                }}
              />
              <Legend 
                wrapperStyle={{ 
                  paddingTop: '5px', 
                  fontSize: '11px',
                  lineHeight: '1.2'
                }}
                iconType="circle"
                layout="horizontal"
                align="center"
                verticalAlign="bottom"
              />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'donut':
        return (
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <PieChart margin={{ top: 20, right: 5, bottom: 5, left: 5 }}>
              <Pie
                data={widget.data}
                cx="50%"
                cy="45%"
                labelLine={false}
                label={({ percent }: any) => `${(percent * 100).toFixed(0)}%`}
                outerRadius={65}
                innerRadius={35}
                fill="#8884d8"
                dataKey="value"
                nameKey="label"
              >
                {widget.data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: number, name: string) => [formatValue(value, widget.format || 'number'), name]} 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '12px'
                }}
              />
              <Legend 
                wrapperStyle={{ 
                  paddingTop: '5px', 
                  fontSize: '11px',
                  lineHeight: '1.2'
                }}
                iconType="circle"
                layout="horizontal"
                align="center"
                verticalAlign="bottom"
              />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={widget.data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="label" 
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                tickFormatter={(value) => formatValue(value, widget.format)}
              />
              <Tooltip 
                formatter={(value: number) => [formatValue(value, widget.format), widget.yLabel || 'Value']}
              />
              <Line type="monotone" dataKey="value" stroke="#0078d4" strokeWidth={2} dot={{ fill: '#0078d4' }} />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={widget.data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="label" 
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                tickFormatter={(value) => formatValue(value, widget.format)}
              />
              <Tooltip 
                formatter={(value: number) => [formatValue(value, widget.format), widget.yLabel || 'Value']}
              />
              <Area type="monotone" dataKey="value" stroke="#0078d4" fill="#0078d4" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'heatmap':
        if (!widget.rows || !widget.columns || !widget.values) return <div>Invalid heatmap data</div>;
        
        const maxValue = Math.max(...widget.values.flat());
        const minValue = Math.min(...widget.values.flat());
        
        return (
          <div className={styles.heatmapGrid} 
               style={{ 
                 gridTemplateColumns: `120px repeat(${widget.columns.length}, 1fr)`,
               }}>
            <div></div>
            {widget.columns.map((col, index) => (
              <div key={index} className={styles.heatmapHeader}>{col}</div>
            ))}
            
            {widget.rows.map((row, rowIndex) => (
              <React.Fragment key={rowIndex}>
                <div className={styles.heatmapRowLabel}>{row}</div>
                {widget.values![rowIndex].map((value, colIndex) => {
                  const intensity = (value - minValue) / (maxValue - minValue);
                  const backgroundColor = `rgba(0, 120, 212, ${intensity})`;
                  return (
                    <div
                      key={colIndex}
                      className={styles.heatmapCell}
                      style={{ backgroundColor }}
                      title={`${row} × ${widget.columns![colIndex]}: ${value.toFixed(2)}`}
                    >
                      {value.toFixed(2)}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        );

      case 'performance':
        const topData = widget.data.filter((item: any) => item.type === 'top');
        const bottomData = widget.data.filter((item: any) => item.type === 'bottom');
        
        return (
          <div className={styles.performanceWidget}>
            <div className={styles.performanceSection}>
              <h4>Top Performers</h4>
              {topData.map((item: any, index: number) => (
                <div key={index} className={styles.performanceItem}>
                  <span className={styles.performanceCategory}>{item.category}</span>
                  <span className={styles.performanceValue}>
                    {formatValue(item.value, widget.format)}
                  </span>
                </div>
              ))}
            </div>
            {bottomData.length > 0 && (
              <div className={styles.performanceSection}>
                <h4>Areas for Improvement</h4>
                {bottomData.map((item: any, index: number) => (
                  <div key={index} className={styles.performanceItem}>
                    <span className={styles.performanceCategory}>{item.category}</span>
                    <span className={styles.performanceValue}>
                      {formatValue(item.value, widget.format)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return <div>Unsupported widget type: {widget.type}</div>;
    }
  };

  if (widget.type === 'kpi') {
    return (
      <div className={`${styles.widget} ${styles.widgetKPI}`}>
        <h3 className={styles.widgetTitle}>{widget.title}</h3>
        <div className={styles.widgetContent}>
          <KPIWidget data={widget.data} />
        </div>
      </div>
    );
  }

  if (widget.type === 'performance') {
    return (
      <div className={`${styles.widget} ${styles.widgetPerformance}`}>
        <h3 className={styles.widgetTitle}>{widget.title}</h3>
        <div className={styles.widgetContent}>
          {renderChart()}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.widget} ${styles[`widget${widget.layout?.size || 'medium'}`]}`}>
      <div className={styles.widgetHeader}>
        <h3 className={styles.widgetTitle}>{widget.title}</h3>
        <div className={styles.widgetControls}>
          {hasViewableData && (
            <TooltipHost content={showData ? "Show Chart" : "Show Data"}>
              <IconButton 
                className={`${styles.viewToggleButton} ${showData ? styles.active : ''}`}
                onClick={() => setShowData(!showData)}
              >
                {showData ? <ChartMultiple20Regular /> : <Table20Regular />}
              </IconButton>
            </TooltipHost>
          )}
          {isSwitchable && !showData && (
            <Dropdown
              className={styles.chartTypeDropdown}
              selectedKey={currentType}
              options={CHART_TYPE_OPTIONS}
              onChange={(_, option) => option && onTypeChange(widgetIndex, option.key as SwitchableChartType)}
              styles={{
                root: { minWidth: 120 },
                dropdown: { fontSize: 12 },
                title: { fontSize: 12, height: 28, lineHeight: '26px' },
                caretDownWrapper: { height: 28, lineHeight: '26px' }
              }}
            />
          )}
        </div>
      </div>
      <div className={styles.widgetContent} style={{ height: showData ? 'auto' : getWidgetHeight(), maxHeight: showData ? '300px' : undefined }}>
        {showData ? (
          <DataTableView data={widget.data} title={widget.title} format={widget.format} />
        ) : (
          renderChart()
        )}
      </div>
    </div>
  );
};

export const Dashboard: React.FC<DashboardProps> = ({ dashboardData }) => {
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<string | null>(null);
  
  // State for chart type overrides - keyed by widget index
  const [chartTypeOverrides, setChartTypeOverrides] = useState<Record<number, SwitchableChartType>>({});
  
  // Handler to change chart type for a widget
  const handleChartTypeChange = useCallback((widgetIndex: number, newType: SwitchableChartType) => {
    setChartTypeOverrides(prev => ({
      ...prev,
      [widgetIndex]: newType
    }));
  }, []);

  console.log('🚀 Dashboard component rendering with data:', dashboardData);
  console.log('🚀 Dashboard widgets count:', dashboardData?.widgets?.length || 0);

  // Export dashboard as image (PNG)
  const handleExportImage = useCallback(async () => {
    if (!dashboardRef.current) return;
    
    setIsExporting(true);
    setExportStatus('Capturing dashboard...');
    
    try {
      // Dynamically import html2canvas to reduce initial bundle size
      const html2canvas = (await import('html2canvas')).default;
      
      const canvas = await html2canvas(dashboardRef.current, {
        backgroundColor: '#f8f9fa',
        scale: 2, // Higher resolution
        useCORS: true,
        logging: false,
        allowTaint: true,
      });
      
      canvas.toBlob((blob) => {
        if (blob) {
          const filename = `${dashboardData.title.replace(/[^a-zA-Z0-9]/g, '_')}_dashboard.png`;
          saveAs(blob, filename);
          setExportStatus('Downloaded!');
        }
      }, 'image/png');
    } catch (error) {
      console.error('Export failed:', error);
      setExportStatus('Export failed');
    } finally {
      setIsExporting(false);
      setTimeout(() => setExportStatus(null), 2000);
    }
  }, [dashboardData.title]);
  
  if (!dashboardData) {
    console.log('❌ No dashboard data provided');
    return <div>No dashboard data available</div>;
  }
  
  if (!dashboardData.widgets || dashboardData.widgets.length === 0) {
    console.log('❌ No widgets in dashboard data');
    return <div>No widgets to display</div>;
  }

  return (
    <div className={styles.dashboard} ref={dashboardRef} data-visual="dashboard">
      <div className={styles.dashboardHeader}>
        <div className={styles.dashboardTitleRow}>
          <h1 className={styles.dashboardTitle}>{dashboardData.title}</h1>
          <div className={styles.exportButtons}>
            {exportStatus && (
              <span className={styles.exportStatus}>{exportStatus}</span>
            )}
            {isExporting ? (
              <Spinner size={SpinnerSize.small} />
            ) : (
              <>
                <TooltipHost content="Download as Image (PNG)" directionalHint={DirectionalHint.topCenter}>
                  <IconButton 
                    className={styles.exportButton}
                    onClick={handleExportImage}
                    disabled={isExporting}
                  >
                    <Image20Regular />
                  </IconButton>
                </TooltipHost>
              </>
            )}
          </div>
        </div>
        <div className={styles.dashboardSummary}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{dashboardData.summary.total_rows.toLocaleString()}</span>
            <span className={styles.summaryLabel}>Records</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{dashboardData.summary.total_columns}</span>
            <span className={styles.summaryLabel}>Columns</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{dashboardData.summary.numeric_columns}</span>
            <span className={styles.summaryLabel}>Numeric</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{dashboardData.summary.categorical_columns}</span>
            <span className={styles.summaryLabel}>Categories</span>
          </div>
        </div>
      </div>

      <div className={styles.widgetGrid}>
        {dashboardData.widgets.map((widget, index) => {
          console.log(`🔧 Rendering widget ${index}:`, widget.type, widget.title);
          return (
            <ChartWidget 
              key={index} 
              widget={widget} 
              widgetIndex={index}
              chartTypeOverrides={chartTypeOverrides}
              onTypeChange={handleChartTypeChange}
            />
          );
        })}
      </div>

      {dashboardData.insights.length > 0 && (
        <div className={styles.insightsSection}>
          <h2 className={styles.insightsTitle}>📊 Key Insights</h2>
          <div className={styles.insightsList}>
            {dashboardData.insights.map((insight, index) => (
              <div key={index} className={styles.insightItem}>
                {insight}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;